// Links between app buildings/users and SoftOne (ERP). Admin only — tenants never see these.
// No link = pending: the admin hasn't decided yet, the UI shows the suggestion.

export type SoftoneLinkStatus = "linked" | "none"
export type SoftoneConfidence = "exact" | "normalized" | "fuzzy"

export interface SoftoneBuilding {
  prjc: string
  code: string
  name: string
  city: string
  address: string
}

export interface SoftoneApartment {
  prjc: string
  code: string
  name: string
  buildingPrjc: string
  fromdate: string | null
  finaldate: string | null
}

export interface SoftoneLink {
  status: SoftoneLinkStatus
  softonePrjc?: string
  softoneCode?: string
  softoneName?: string
  source?: "suggestion" | "manual" | "backfill"
  confidence?: SoftoneConfidence | null
  linkedBy?: string
  linkedAt?: string
}

export interface SoftoneSuggestion {
  softonePrjc: string
  softoneCode: string
  softoneName: string
  confidence: SoftoneConfidence
  reason: string
}

export interface SoftoneBuildingLinkItem {
  buildingId: string
  link: SoftoneLink | null
  missing: boolean
  suggestion: SoftoneSuggestion | null
  note: string
}

export interface SoftoneUserLinkItem {
  userId: string
  softoneBuildingPrjc: string | null
  link: SoftoneLink | null
  stale: boolean
  suggestion: SoftoneSuggestion | null
  note: string
}

// Latin letters that look like Greek capitals (same map as the backend matcher, softone-match.ts)
const LATIN = "ABEHIKMNOPTXYZ"
const GREEK = "ΑΒΕΗΙΚΜΝΟΡΤΧΥΖ"

/** Search key for SoftOne codes/names: no accents, upper case, Latin look-alikes → Greek, no separators.
 *  So "gm 06", "ΓΜ06" and "1009-γμ06" all find 1009-ΓΜ06. */
export const softoneSearchKey = (s: string): string =>
  s
    .normalize("NFD")
    .replace(/\p{Mn}/gu, "")
    .toUpperCase()
    .replace(/[A-Z]/g, (c) => (LATIN.includes(c) ? GREEK[LATIN.indexOf(c)] : c))
    .replace(/[^\p{L}\p{N}]+/gu, "")

/** Apartment number without the building code: '1009-ΓΜ06' → 'ΓΜ06', '1004_Μ01' → 'Μ01'. */
export const softoneUnitNumber = (code: string): string => code.replace(/^\d+\s*[-_]\s*/, "") || code

// softoneRequests/{ticketId}: what happened when the ticket was sent to SoftOne
export type SoftoneRequestStatus = "disabled" | "waiting_link" | "sending" | "sent" | "failed"

export interface SoftoneRequestRecord {
  ticketId: string
  status: SoftoneRequestStatus
  soaction?: string
  trdr?: string | null
  imagesSent?: number
  imagesAccepted?: number | null
  attempts?: number
  error?: string | null
  // waiting_link only: the user is linked now, so sending again will work
  linkReady?: boolean
  sentAt?: string
  updatedAt?: string
}
